import DynamicForm from '@remote-com/mushu/src/components/Form/DynamicForm';
import { supportedTypes } from '@remote-com/mushu/src/components/Form/DynamicForm/helpers';
import {
  SuccessTitle,
  SuccessDescription,
  SuccessActions,
} from '@remote-com/mushu/src/components/Success';
import SuccessMark from '@remote-com/mushu/src/components/Success/SuccessMark';
import { GlobalStyles, theme as mushuTheme } from '@remote-com/mushu/src/config/styles';
import {
  baseString,
  emailSchema,
  genericRequiredLabel,
} from '@remote-com/mushu/src/helpers/validationSchema';
import { useFormEvents } from '@remote-com/mushu/src/hooks/useFormEvents';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, ButtonInline } from 'remote-com-shared/components/Button';
import { dataLayerPush } from 'remote-com-shared/components/CookieScripts/helpers';
import Modal from 'remote-com-shared/components/ModalForm';
import {
  getCurrentURL,
  registerLead,
  createPartnerStackSignup,
} from 'remote-com-shared/components/ModalForm/helpers/services';
import {
  FormContainer,
  SubmitButton,
  CaptchaWrapper,
  SubsribeSuccessContainer,
  ModalTitle,
  FormError,
  DynamicFormWrapper,
  SubmitButtonWrapper,
  ModalDescription,
} from 'remote-com-shared/components/ModalForm/styles';
import { hasImpactFields } from 'remote-com-shared/domains/impact/helpers';
import { LocaleContextConsumer } from 'remote-com-shared/domains/locales/context';
import { useImpactFormValues } from 'remote-com-shared/domains/shared/trackingParameters';
import { getPartnerStackInfo } from 'remote-com-shared/utils/partnerstack';
import { ThemeProvider } from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import { boolean } from 'yup';

import { FormWrapper, FormLabel } from './styles';

function HubSpotRichText({ content, type }) {
  if (!content.length) {
    return null;
  }

  if (type === 'TEXT') {
    // eslint-disable-next-line react/no-danger
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  }

  return null;
}

function getFields(formFieldGroups) {
  return formFieldGroups.reduce((fieldsList, fieldGroup) => {
    if (fieldGroup.richText.content) {
      fieldsList.push({
        type: supportedTypes.EXTRA,
        Component() {
          return <HubSpotRichText {...fieldGroup.richText} />;
        },
      });
    }

    fieldGroup.fields.forEach(({ fieldType, ...field }) => {
      const { hidden, name, defaultValue } = field;

      if (hidden) {
        fieldsList.push({
          type: supportedTypes.HIDDEN,
          name,
          value: defaultValue,
        });
        return;
      }

      switch (fieldType) {
        case 'text': {
          const { label, required } = field;

          const schema = name === 'email' ? emailSchema : baseString;

          if (!hidden) {
            fieldsList.push({
              type: name === 'email' ? supportedTypes.EMAIL : supportedTypes.TEXT,
              name,
              label,
              required,
              schema: required ? schema.required(genericRequiredLabel) : schema,
            });
          }
          break;
        }

        case 'booleancheckbox': {
          const { label, required } = field;
          fieldsList.push({
            type: supportedTypes.ACK_CHECK,
            schema: boolean(),
            required,
            name,
            // eslint-disable-next-line react/no-danger
            label: <FormLabel dangerouslySetInnerHTML={{ __html: label }} />,
            defaultValue: true,
          });
          break;
        }

        default:
      }
    });

    return fieldsList;
  }, []);
}

export function GatedContentForm({
  hubspotFormDefinition,
  gatedFile,
  gatedFileDownloadLabel,
  inModal = false,
}) {
  const { submitText, inlineMessage: successMessage, formFieldGroups } = hubspotFormDefinition;

  const [showLoader, setShowLoader] = useState(false);
  const [showFormError, setShowFormError] = useState(false);
  const [isCaptchaDone, setIsCaptchaDone] = useState(false);
  const [showSuccessState, setShowSuccessState] = useState(false);

  const { formState } = useFormEvents();
  const [formValues, setFormValues] = useState({});

  const reCaptchaRef = useRef(null);
  const impactFormValues = useImpactFormValues();
  const fields = getFields(formFieldGroups);

  function handleCaptchaChange(value) {
    setIsCaptchaDone(value);
  }

  async function handleSubmit(values, { setSubmitting }) {
    setShowLoader(true);

    const updatedFormValues = {
      ...formValues,
      ...values,
    };
    setFormValues(updatedFormValues);

    if (isCaptchaDone) {
      const includePartnerstack =
        Object.prototype.hasOwnProperty.call(values, 'pspartnerkey') &&
        Object.prototype.hasOwnProperty.call(values, 'pspartner_group');
      const includeImpactValues = hasImpactFields(values);

      try {
        if (includePartnerstack) {
          try {
            const publicSlug = uuid4();
            await createPartnerStackSignup({
              name: values.name,
              email: publicSlug,
            });
          } catch {
            // Ignore as failing should not break hubspot
          }
        }

        const payload = {
          ...values,
          ...(Object.prototype.hasOwnProperty.call(values, 'utm')
            ? {
                utm: getCurrentURL(),
              }
            : {}),
          ...(includePartnerstack ? getPartnerStackInfo() : {}),
          ...(includeImpactValues ? impactFormValues : {}),
        };

        await registerLead(
          payload,
          `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotFormDefinition.portalId}/${hubspotFormDefinition.guid}/`
        );

        dataLayerPush({
          event: 'user-submitted-hubspot-form',
          label: window.location.pathname,
          conversion: { send_to: 'AW-594140685/SLovCLa8nOEBEI28p5sC' },
        });

        setShowLoader(false);
        setShowSuccessState(true);
      } catch (exception) {
        reCaptchaRef.current.reset();
        setIsCaptchaDone(false);
        setShowFormError(true);
        setShowLoader(false);
        setSubmitting(false);

        throw exception;
      }
    }
  }

  return (
    <ThemeProvider theme={{ mode: 'light', ...mushuTheme }}>
      <GlobalStyles />
      <FormContainer>
        {showSuccessState ? (
          <SubsribeSuccessContainer>
            <SuccessMark />
            <SuccessTitle>Thank you!</SuccessTitle>
            <SuccessDescription dangerouslySetInnerHTML={{ __html: successMessage }} />
            <SuccessActions>
              {gatedFile && (
                <Button href={gatedFile.url} target={gatedFile.target}>
                  {gatedFileDownloadLabel}
                </Button>
              )}
            </SuccessActions>
            <SuccessDescription>
              <p>
                Want to see a demo of the Remote platform to manage payroll, benefits, and more for
                your employees and contractors around the world?
              </p>
            </SuccessDescription>
            <SuccessActions>
              <Button href={`${process.env.NEXT_PUBLIC_PANDA_BASE_URL}/?schedule_demo_form=1`}>
                Schedule a demo
              </Button>
            </SuccessActions>
          </SubsribeSuccessContainer>
        ) : (
          <DynamicFormWrapper>
            <ModalTitle>Download the Choose Remote Toolkit</ModalTitle>
            <ModalDescription>
              Learn how you can stay remote or hybrid and thrive in the remote workforce!
            </ModalDescription>

            {showFormError && (
              <FormError>
                Something went wrong. Please try again, or contact{' '}
                <ButtonInline href="mailto:help@remote.com">help@remote.com</ButtonInline> if this
                error persists. Thank you.
              </FormError>
            )}

            <DynamicForm
              formName={`${hubspotFormDefinition.guid}${inModal ? '-modal' : ''}`}
              fields={fields}
              fieldValues={formValues}
              formError={formState.errorMessage}
              formTheme="dark"
              formSize="small"
              handleOnSubmit={handleSubmit}
              isLoading={formState.isLoading}
              enableReinitialize
            />

            <CaptchaWrapper>
              <LocaleContextConsumer>
                {(locale) => (
                  <ReCAPTCHA
                    hl={locale}
                    ref={reCaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_CAPTCHA_KEY}
                    onChange={handleCaptchaChange}
                  />
                )}
              </LocaleContextConsumer>
            </CaptchaWrapper>

            <SubmitButtonWrapper>
              <SubmitButton
                isLoading={showLoader}
                disabled={!isCaptchaDone}
                form={`${hubspotFormDefinition.guid}${inModal ? '-modal' : ''}`}
              >
                {submitText}
              </SubmitButton>
            </SubmitButtonWrapper>
          </DynamicFormWrapper>
        )}
      </FormContainer>
    </ThemeProvider>
  );
}

export const GatedContentFormWrapped = (props) => (
  <FormWrapper>
    <GatedContentForm {...props} />
  </FormWrapper>
);

export const GatedContentFormModal = ({ visible, onCancel, ...props }) => (
  <Modal visible={visible} width="660px" footer={null} onCancel={onCancel}>
    <GatedContentForm {...props} inModal />
  </Modal>
);
